import React from 'react';
import { Check, ArrowRight, Users, MapPin, Video, Clock, Sparkles, Target, MessageSquare, Lightbulb } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Workshops() {
  const {
    t,
    language
  } = useLanguage();

  // Primary workshops - core offerings linked to content
  const primaryWorkshops = [{
    id: 'team-dynamics',
    icon: <Users size={32} data-id="element-1801" data-mp="1mrrrveab6g5f" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    tKey: 'teamDynamics',
    path: null
  }, {
    id: 'leadership-intensive',
    icon: <Sparkles size={32} data-id="element-1802" data-mp="3h36kq3ym8vpe" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    tKey: 'leadershipIntensive',
    path: null
  }, {
    id: 'strategy',
    icon: <Target size={32} data-id="element-1803" data-mp="1ubu8kd8mlts3" />,
    color: 'bg-yellow-500',
    lightColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
    tKey: 'strategy',
    path: '/services/workshops/strategy'
  }, {
    id: 'feedback-culture',
    icon: <MessageSquare size={32} data-id="element-1804" data-mp="2r1bqbqkvv6a5" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    tKey: 'feedbackCulture',
    path: '/services/workshops/feedback-culture'
  }];

  // Secondary - facilitation skills, scheduled on demand
  const secondaryWorkshops = [{
    id: 'virtual-facilitation',
    icon: <Video size={32} data-id="element-1805" data-mp="3apkhbxaox682" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    tKey: 'virtualFacilitation'
  }, {
    id: 'coaching-skills',
    icon: <Lightbulb size={32} data-id="element-1806" data-mp="3fnhrzzv6hf7r" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    tKey: 'coachingSkills',
    path: '/services/workshops/coaching-skills'
  }];
  const isIS = language === 'is';

  // Translation helpers for new workshop content
  const strategyContent = {
    title: isIS ? 'Stefnum\u00f3tunarvinnustofa' : 'Strategy Facilitation Workshop',
    subtitle: isIS ? 'Fyrir forystu teymi' : 'For leadership teams',
    description: isIS ? 'Flestir sem fara á ráðstefnur koma heim með skjal. Þessi vinnustofa er hönnuð til að framleiða eitthvað gagnlegara: raunverulega stefnu - skýra val um hvar við spilum og hvernig við vinnum, með erfiðar málaróðar gerðar skýrar.' : 'Most strategy offsites produce a document. This workshop is designed to produce something more useful: an actual strategy \u2014 a clear choice about where to play and how to win, with the hard trade-offs made explicit.',
    duration: isIS ? '1-2 dagar' : '1-2 days',
    format: isIS ? 'Á staðnum' : 'On-site',
    benefits: isIS ? ['Sameiginleg greining á raunverulegri stöðu', 'Skýrar ákvarðanir um forgangsmál', 'Stefnulegar rók sem allir geta byggð á', 'Næstu skref með skýra ábyrgurð'] : ['Shared diagnosis of the situation you are actually in', 'Explicit choices about where to focus', 'Strategic logic the whole team can articulate', 'Immediate next steps with ownership']
  };
  const feedbackContent = {
    title: isIS ? 'Menning endurgjöfar' : 'Feedback Culture Workshop',
    subtitle: isIS ? 'Fyrir teymi og stjórnendur' : 'For teams and managers',
    description: isIS ? 'Endurgjöfarmenning sem virkar er ekki byggð á fleiri endurgjöf. Hún er byggð á betri skilning á hvað endurgjöf gerir - og hvað hjálpar fólki að læra og þróast í raun.' : 'A feedback culture that works is not built on more feedback. It is built on a better understanding of what feedback actually does \u2014 and what genuinely helps people learn and develop.',
    duration: isIS ? 'Hálfur dagur / 3 lotur' : 'Half day / 3-session series',
    format: isIS ? 'Á staðnum eða á netinu' : 'On-site or online',
    benefits: isIS ? ['Hvers vegna hefðbundið endurgjöfarlikan virkar gegn námi', 'Hvað jákvæð endurgjöf er raunverulega', 'Hvernig á að hafa erfið samtöl án varnarsvipu', 'Hagnýt verkfæri sem teymið getur notað strax'] : ['Why the standard feedback model works against learning', 'What appreciative feedback actually is', 'How to have difficult conversations without the defensive response', 'Practical tools the team can use the following week']
  };
  const coachingSkillsContent = {
    title: isIS ? 'Þjálfunartækni fyrir stjórnendur' : 'Coaching Skills for Managers',
    subtitle: isIS ? 'Námskeið í 6 lotum' : '6-session programme',
    description: isIS ? 'Að vera forvitinn lengur áður en þú gefur ráð breytir eignlegar öllu um leiðtogi og þjálfun. En advice monster - þann næður heim við ráð - er erfið í öxlinni.' : 'Staying curious longer before giving advice changes everything about leadership. But the advice monster \u2014 the deeply conditioned habit of moving straight to solutions \u2014 is hard to interrupt without deliberate practice.',
    duration: isIS ? '6 x 2 klukkustundir' : '6 x 2 hours',
    format: isIS ? 'Á netinu' : 'Online',
    benefits: isIS ? ['Sjö spurningarnar úr The Coaching Habit', 'Að rjúfa advice monster viðbragðið', 'Clean spurningar og hæfn í hlustuninni', 'Hagnýt verkfæri fyrir 1:1 fundi sem þróa'] : ['The seven questions from The Coaching Habit', 'Recognising and interrupting the advice monster', 'Clean questions and listening skills', 'Practical tools for 1:1s that develop people']
  };
  const renderCard = (workshop: typeof primaryWorkshops[0], index: number, isLinked = false, extraContent?: {
    title: string;
    subtitle: string;
    description: string;
    duration: string;
    format: string;
    benefits: string[];
  }) => {
    const isCustom = !!extraContent;
    const title = isCustom ? extraContent!.title : t(`workshops.items.${workshop.tKey}.title`);
    const subtitle = isCustom ? extraContent!.subtitle : t(`workshops.items.${workshop.tKey}.subtitle`);
    const description = isCustom ? extraContent!.description : t(`workshops.items.${workshop.tKey}.description`);
    const duration = isCustom ? extraContent!.duration : t(`workshops.items.${workshop.tKey}.duration`);
    const format = isCustom ? extraContent!.format : t(`workshops.items.${workshop.tKey}.format`);
    const benefits = isCustom ? extraContent!.benefits : t(`workshops.items.${workshop.tKey}.benefits`) as string[];
    const card = <Card className={`p-0 overflow-hidden border-2 ${workshop.borderColor} ${isLinked && workshop.path ? 'hover:shadow-lg transition-shadow' : ''}`} data-id="element-1807" data-mp="2oj9w1pdkcp84">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-1808" data-mp="2jpngnpg0m78m">
          <div className={`lg:col-span-4 ${workshop.lightColor} p-10 flex flex-col justify-center items-center text-center`} data-id="element-1809" data-mp="2r47m8h9vx12a">
            <div className={`w-20 h-20 rounded-full ${workshop.color} text-white flex items-center justify-center mb-6 shadow-lg`} data-id="element-1810" data-mp="2w3ki1riqhih0">
              {workshop.icon}
            </div>
            <h3 className={`text-2xl font-bold ${workshop.textColor} mb-2`} data-id="element-1811" data-mp="353qs13f840bf">{title}</h3>
            <p className="font-medium text-gray-600" data-id="element-1812" data-mp="3a0zbr8g0sces">{subtitle}</p>
          </div>
          <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-1813" data-mp="3ezk2mh9dtjqk">
            <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1814" data-mp="3jr25jauytue6">{description}</p>
            <div className="flex flex-wrap gap-4 mb-6 text-sm" data-id="element-1815" data-mp="3oqwsj3bmdzty">
              <div className="flex items-center gap-2 text-gray-500" data-id="element-1816" data-mp="3os7y98omlhnp"><Clock size={16} className={workshop.textColor} data-id="element-1817" data-mp="3st1o47lmp6j8" /><span data-id="element-1818" data-mp="3sq5psi5vcfu9">{duration}</span></div>
              <div className="flex items-center gap-2 text-gray-500" data-id="element-1819" data-mp="1mqrcnnnirxp"><MapPin size={16} className={workshop.textColor} data-id="element-1820" data-mp="1jcl34bv9ykz" /><span data-id="element-1821" data-mp="mvm6wy42fsqr">{format}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-id="element-1822" data-mp="hxkrcn5umbk1">
              {(benefits as string[]).map((benefit, i) => <div key={i} className="flex items-start" data-id="element-1823" data-mp="wtscbpp2zh0d" data-mpi={i}>
                  <div className={`mt-1 mr-3 p-1 rounded-full ${workshop.lightColor} ${workshop.textColor}`} data-id="element-1824" data-mp="rvqraz83h7m9"><Check size={14} strokeWidth={3} data-id="element-1825" data-mp="rtw8bqgdvpkt" /></div>
                  <span className="text-gray-700 font-medium" data-id="element-1826" data-mp="46xtex7wwpp0">{benefit}</span>
                </div>)}
            </div>
            <div className="pt-6 border-t border-gray-100 flex gap-3" data-id="element-1827" data-mp="3vacs8su1kfm3">
              <Link to="/contact" data-id="element-1828" data-mp="dxiowbsahe3p">
                <Button variant={index % 2 === 1 ? 'secondary' : 'primary'} rightIcon={<ArrowRight size={18} data-id="element-1830" data-mp="93xz370unqfl" />} data-id="element-1829" data-mp="dxscxttrxb6r">
                  {t('workshops.getQuote')}
                </Button>
              </Link>
              {workshop.path && <Link to={workshop.path} data-id="element-1831" data-mp="3cmr610z4hh0s">
                  <Button variant="outline" rightIcon={<ArrowRight size={16} data-id="element-1833" data-mp="1s3f2ueemvu58" />} data-id="element-1832" data-mp="3cjvcdoub5ei5">
                    {isIS ? 'Nánari upplýsingar' : 'More information'}
                  </Button>
                </Link>}
            </div>
          </div>
        </div>
      </Card>;
    return <Animate key={workshop.id} delay={index * 0.08} data-id="element-1834" data-mp="1i7bzj581ppgp">{card}</Animate>;
  };
  return <PageTransition data-id="element-1835" data-mp="1n5lzru8mng96">
      <div className="pt-12 pb-24" data-id="element-1836" data-mp="1n53sm2kffhmh">
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-1837" data-mp="2auoeat7ysszg">
          <DecorativeShape type="circle" color="bg-coral/10" className="top-0 left-0 w-40 h-40 -z-10" data-id="element-1838" data-mp="2aslrei23zfyy" />
          <DecorativeShape type="square" color="bg-teal/10" className="bottom-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-1839" data-mp="21tqk12nbvzu2" />
          <Animate data-id="element-1840" data-mp="25usktnww8jy5">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-1841" data-mp="25uage402uzf5">{t('workshops.badge')}</span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-1842" data-mp="qgo3g7l8ba9w">{t('workshops.title')} <br data-id="element-1843" data-mp="uhq3urqvtsdg" /><span className="text-coral" data-id="element-1844" data-mp="uhq32pj25oce">{t('workshops.titleHighlight')}</span></h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1845" data-mp="1vv7dw5ihw2ol">{t('workshops.subtitle')}</p>
          </Animate>
        </section>

        {/* Primary workshops */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-1846" data-mp="1n243k635c5y6">
          <Animate className="flex items-center gap-4 mb-12" data-id="element-1847" data-mp="1mzrllsxe1vl4">
            <div className="p-3 bg-coral/10 rounded-2xl" data-id="element-1848" data-mp="1r1mnozn58czy"><MapPin size={28} className="text-coral" data-id="element-1849" data-mp="1d3eki9zikfgc" /></div>
            <div data-id="element-1850" data-mp="1d1trwpwrf6l4">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1851" data-mp="1d5hgrbr9f4ti">{t('workshops.inPersonTitle')}</h2>
              <p className="text-gray-500" data-id="element-1852" data-mp="1i0eqkkwd5i7g">{t('workshops.inPersonSubtitle')}</p>
            </div>
          </Animate>
          <div className="space-y-8" data-id="element-1853" data-mp="184br5mswbxo7">
            {renderCard(primaryWorkshops[0], 0)}
            {renderCard(primaryWorkshops[1], 1)}
            {renderCard(primaryWorkshops[2], 2, true, strategyContent)}
            {renderCard(primaryWorkshops[3], 3, true, feedbackContent)}
          </div>
        </section>

        {/* Secondary / facilitation skills */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-1854" data-mp="qvitg8dwrcww">
          <Animate className="mb-8" data-id="element-1855" data-mp="muff5qbidoxs">
            <h2 className="text-2xl font-bold text-charcoal" data-id="element-1856" data-mp="mrb5wej6s309">
              {isIS ? 'Fagnámskeið og stærri lóðsunarhæfni' : 'Facilitation skills & specialist programmes'}
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-1857" data-mp="vsrfik26g13f">
              {isIS ? 'Eftirfarandi eru sérhæfð námskeið sem eru skipuleguð eftir eftirspurn - hafu samband ef þú hefur húgi á ðessum.' : 'The following are specialist programmes scheduled on demand \u2014 get in touch if you are interested.'}
            </p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1858" data-mp="1ekbvmml4igzy">
            {/* Online facilitation */}
            <Animate delay={0} data-id="element-1859" data-mp="3la8dwnnmcscr">
              <Card className="p-6 border-2 border-blue-200 h-full flex flex-col" data-id="element-1860" data-mp="3lb9zvn66az99">
                <div className="p-3 bg-blue-50 rounded-2xl w-fit mb-4" data-id="element-1861" data-mp="3gd8das9jght1"><Video size={24} className="text-blue-600" data-id="element-1862" data-mp="1k920ofbp7ynk" /></div>
                <span className="text-xs font-bold uppercase tracking-wide text-blue-600 mb-1" data-id="element-1863" data-mp="1kawoq2i9o6vv">
                  {isIS ? 'Fjarfundir og vinnustofur á netinu' : 'Online meetings & workshops'}
                </span>
                <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-1864" data-mp="1u9cfg4ddte4u">
                  {isIS ? 'Betri fjarfundir og vinnustofur á netinu' : 'Better Online Meetings & Workshops'}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-1865" data-mp="235tql7lqxc0l">
                  {isIS ? 'Hönnun og lóðsun lifandi funda og vinnustofa á netinu með Miro, breakout rooms og Training from the Back of the Room aðferðafræði.' : 'Designing and facilitating live online meetings and workshops using Miro, breakout rooms, and Training from the Back of the Room methodology.'}
                </p>
                <Link to="/services/courses/better-online-meetings" data-id="element-1866" data-mp="2qyaazosyhg32">
                  <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-1868" data-mp="fbgj42td4wi0" />} data-id="element-1867" data-mp="f9n9cengdw5s">
                    {isIS ? 'Nánari upplýsingar' : 'More information'}
                  </Button>
                </Link>
              </Card>
            </Animate>
            {/* Coaching skills */}
            <Animate delay={0.1} data-id="element-1869" data-mp="1a47u4p5dj2g">
              <Card className="p-6 border-2 border-teal/20 h-full flex flex-col" data-id="element-1870" data-mp="1b5rgxk8bdvs">
                <div className="p-3 bg-teal/10 rounded-2xl w-fit mb-4" data-id="element-1871" data-mp="y0wpv7vn5l6v"><Lightbulb size={24} className="text-teal" data-id="element-1872" data-mp="y2hgimeu4jzg" /></div>
                <span className="text-xs font-bold uppercase tracking-wide text-teal mb-1" data-id="element-1873" data-mp="u02yg4izpo98">
                  {isIS ? 'Leiðtogaþróun' : 'Leadership development'}
                </span>
                <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-1874" data-mp="k9rtqfrhfb1a">
                  {isIS ? 'Þjálfunartækni fyrir stjórnendur' : 'Coaching Skills for Managers'}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-1875" data-mp="1cx447rkdbse8">
                  {isIS ? 'Sex lota námskeið sem þróar þjálfunarnálgun við leiðtogi. Að vera forvitinn lengur, spyrja betri spurninga, og skapa aðstæður þar sem fólk getur leyst eigin vandamál.' : 'A six-session programme developing a coaching approach to leadership. Staying curious longer, asking better questions, creating conditions where people can solve their own problems.'}
                </p>
                <Link to="/services/workshops/coaching-skills" data-id="element-1876" data-mp="1g76vxsxuhmyp">
                  <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-1878" data-mp="1k77iy1z0f6hd" />} data-id="element-1877" data-mp="1k7h8jog57bmj">
                    {isIS ? 'Nánari upplýsingar' : 'More information'}
                  </Button>
                </Link>
              </Card>
            </Animate>
          </div>
        </section>

        {/* Custom CTA */}
        <section className="max-w-4xl mx-auto px-4 mt-8 text-center" data-id="element-1879" data-mp="1z4ggjdewp6hj">
          <Animate data-id="element-1880" data-mp="23vq0bxdrxfli">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-teal/5 border-0" data-id="element-1881" data-mp="23w86blq8n86m">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1882" data-mp="2da9z4806mao1">{t('workshops.customTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1883" data-mp="2d85o763v8be9">{t('workshops.customDescription')}</p>
              <Link to="/contact" data-id="element-1884" data-mp="2n0bmexkpnp1w"><Button variant="primary" rightIcon={<ArrowRight size={18} data-id="element-1886" data-mp="2mztidert46jf" />} data-id="element-1885" data-mp="2n0uulmkk6z2q">{t('workshops.customCta')}</Button></Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}